import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import searchAnimation from "../../Assets/Loader/search.json";
import { FiShield } from "react-icons/fi";
import { verifyOtp, sendOtp } from "../../Services/AuthenticationService";
import DriftWall from "../../Components/SignIn/DriftWall";
import { APPRoutes } from "../../Utils/Route";
import { toast } from "react-toastify";
import { useAuth } from "../../Context/AuthContext";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

const picsContext = require.context("../../Assets/Images/Driftwall_images", false, /\.jpe?g$/);
const DRIFT_WALL_ITEMS = picsContext.keys().map((key) => ({ image: picsContext(key) }));

const maskEmail = (email) => {
  const [name, domain] = (email || "").split("@");
  if (!name || !domain) return email || "";
  const visible = name.slice(0, Math.min(2, name.length));
  return `${visible}${"*".repeat(Math.max(1, name.length - visible.length))}@${domain}`;
};

const getRemainingCooldown = (user) => {
  if (!user) return 0;
  const savedExpiry = sessionStorage.getItem(`mfa_otp_expiry_${user}`);
  if (!savedExpiry) {
    const newExpiry = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
    sessionStorage.setItem(`mfa_otp_expiry_${user}`, newExpiry.toString());
    return RESEND_COOLDOWN_SECONDS;
  }
  const remaining = Math.ceil((Number(savedExpiry) - Date.now()) / 1000);
  return remaining > 0 ? remaining : 0;
};

const MFAVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();

  const username = location.state?.username || sessionStorage.getItem("mfa_pending_username") || "";

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [otpError, setOtpError] = useState("");
  const [verifyState, setVerifyState] = useState("idle"); // idle | loading | success | error
  const [resendState, setResendState] = useState("idle"); // idle | loading
  const [resendCooldown, setResendCooldown] = useState(() => getRemainingCooldown(username));
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!username) {
      navigate(APPRoutes.LOGIN, { replace: true });
    }
  }, [username, navigate]);

  useEffect(() => {
    if (!username) return undefined;

    const updateCooldown = () => {
      const savedExpiry = sessionStorage.getItem(`mfa_otp_expiry_${username}`);
      if (savedExpiry) {
        const remaining = Math.ceil((Number(savedExpiry) - Date.now()) / 1000);
        setResendCooldown(remaining > 0 ? remaining : 0);
      } else {
        setResendCooldown(0);
      }
    };

    updateCooldown();
    const timer = setInterval(updateCooldown, 1000);
    return () => clearInterval(timer);
  }, [username]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const focusInput = (index) => {
    inputsRef.current[index]?.focus();
    inputsRef.current[index]?.select();
  };

  const handleChange = (index, rawValue) => {
    const value = rawValue.replace(/\D/g, "");
    if (!value) {
      setOtp((prev) => {
        const next = [...prev];
        next[index] = "";
        return next;
      });
      return;
    }

    setOtp((prev) => {
      const next = [...prev];
      next[index] = value[value.length - 1];
      return next;
    });
    setOtpError("");

    if (index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    event.preventDefault();
    const next = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((digit, i) => {
      next[i] = digit;
    });
    setOtp(next);
    setOtpError("");
    focusInput(Math.min(pasted.length, OTP_LENGTH - 1));
  };

  const renderVerifyButtonContent = () => {
    if (verifyState === "loading") {
      return (
        <Lottie
          animationData={searchAnimation}
          loop={true}
          style={{ width: "3.5rem", height: "3.5rem" }}
        />
      );
    }
    return "Verify";
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    const code = otp.join("");

    if (code.length !== OTP_LENGTH) {
      setOtpError("Enter the complete 6-digit code");
      return;
    }

    setVerifyState("loading");
    try {
      const response = await verifyOtp({ email: username, otp: code });
      if (response?.is_success) {
        sessionStorage.removeItem(`mfa_otp_expiry_${username}`);
        sessionStorage.removeItem("mfa_pending_username");
        authLogin(response?.data);
        setVerifyState("success");
        navigate(APPRoutes.DASHBOARD);
      } else if (response?.is_success === false && response?.status_code === 500) {
        setVerifyState("error");
        toast.error("Internal Server Error. Please Contact Support Team.");
      } else {
        setVerifyState("error");
        setOtpError(response?.message || "Invalid or expired code");
        toast.error(response?.message || "Invalid or expired code. Please try again.");
      }
    } catch (error) {
      setVerifyState("error");
      toast.error(error?.message || "An unexpected error occurred. Please try again later.");
      console.error("OTP verification error:", error);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendState === "loading") return;

    setResendState("loading");
    try {
      const response = await sendOtp(username);
      if (response?.is_success) {
        toast.success(response?.message || "A new code has been sent.");
        setOtp(Array(OTP_LENGTH).fill(""));
        setOtpError("");
        const newExpiry = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
        sessionStorage.setItem(`mfa_otp_expiry_${username}`, newExpiry.toString());
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        focusInput(0);
      } else {
        toast.error(response?.message || "Failed to resend code. Please try again.");
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again later.");
      console.error("Resend OTP error:", error);
    } finally {
      setResendState("idle");
    }
  };

  if (!username) {
    return null;
  }

  return (
    <div data-bs-theme="light" className="hc-auth-shell position-relative min-vh-100 w-100 overflow-hidden">
      <div className="hc-auth-driftwall-wrap position-absolute">
        <DriftWall items={DRIFT_WALL_ITEMS} fade={0.05} />
      </div>

      <div className="hc-auth-center position-relative min-vh-100 w-100 d-flex align-items-center justify-content-center pe-none">
        <div className="hc-auth-panel position-relative w-100 d-flex flex-column justify-content-center pe-auto overflow-hidden">
          <div className="hc-auth-header d-flex flex-column align-items-start">
            <div className="hc-auth-hero-pill align-self-start d-inline-flex align-items-center py-1 px-3 rounded-pill text-nowrap pe-none">
              <FiShield size={14} className="flex-shrink-0" />
              <span>Secure connection</span>
            </div>

            <div className="hc-auth-header-row d-flex align-items-center w-100">
              <img
                src="https://www.aegishs.us/aegis-img/badge_logo_ring.png"
                alt="Aegis HS 360"
                className="hc-auth-logo object-fit-contain"
              />
              <div className="hc-auth-header-text flex-fill">
                <h2 className="hc-auth-title text-start m-0">OTP <span className="hc-auth-title-accent">Verification</span></h2>
                <p className="hc-auth-subtitle text-start mt-1 mx-0 mb-0">Please enter your 6 digit OTP</p>
              </div>
            </div>
          </div>

          <div className="hc-auth-panel-inner">
            <form onSubmit={handleVerify} noValidate>
              <p className="hc-auth-otp-hint text-center">
                Please check your email ({maskEmail(username)}) for the OTP
              </p>
              <div className="hc-auth-otp-row d-flex justify-content-center mt-1 mx-0 mb-2" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputsRef.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    className={`hc-auth-otp-box text-center fw-bold${otpError ? " is-invalid" : ""}`}
                    value={digit}
                    onChange={(event) => handleChange(index, event.target.value)}
                    onKeyDown={(event) => handleKeyDown(index, event)}
                    aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
                  />
                ))}
              </div>
              <div className="hc-auth-field-error text-center">{otpError}</div>

              <button
                type="submit"
                className={`btn hc-auth-submit-btn rounded-pill mt-2 w-100 fw-semibold border-0 fs-5 d-flex align-items-center justify-content-center ${verifyState === "loading" ? "p-0" : "py-2"}`}
                disabled={verifyState === "loading" || verifyState === "success"}
                style={{
                  color: "var(--hc-white)",
                  boxShadow: "0 14px 32px rgba(var(--hc-black-rgb), 0.25)",
                  opacity: verifyState === "loading" ? 0.7 : 1,
                  minHeight: "3rem",
                }}
              >
                {renderVerifyButtonContent()}
              </button>
            </form>

            <p className="mt-3 mb-0 text-center fs-6 hc-auth-footer-text">
              Didn't receive the code?
              <button
                type="button"
                className="ms-1 text-decoration-none text-nowrap hc-auth-footer-link fw-bold border-0 p-0"
                style={{ background: "transparent" }}
                onClick={handleResend}
                disabled={resendCooldown > 0 || resendState === "loading"}
              >
                {resendState === "loading"
                  ? "Sending..."
                  : resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend code"}
              </button>
            </p>

            <p className="hc-auth-trust-line d-flex align-items-center justify-content-center text-nowrap">
              <FiShield size={13} />
              Secured with encryption &middot; HIPAA compliant
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAVerification;
